import React from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import CopyButton from '../../../../components/NaturaOps/CopyButton'
import JsonBlock from '../../../../components/NaturaOps/JsonBlock'
import { buildTicketText, formatTimestamp } from '../utils/ticketText'
import { DELIVERY_DELIVERED, DELIVERY_NOT_DELIVERED, DELIVERY_UNKNOWN } from '../constants'

const DELIVERY_STYLE = {
  [DELIVERY_DELIVERED]: 'border-green-200 bg-green-50 text-green-800',
  [DELIVERY_NOT_DELIVERED]: 'border-red-200 bg-red-50 text-red-800',
  [DELIVERY_UNKNOWN]: 'border-amber-200 bg-amber-50 text-amber-800',
}

const OutcomeHeading = ({ error }) => {
  const { t } = useTranslation()
  const key = error ? `outcome_${error}` : 'outcome_accepted'
  const tone = error ? 'text-red-800' : 'text-green-800'
  return <h2 className={`text-lg font-semibold mb-2 ${tone}`}>{t(`natura_operations.confirmation_resend.${key}`)}</h2>
}

OutcomeHeading.propTypes = { error: PropTypes.string }
OutcomeHeading.defaultProps = { error: null }

export default function EvidencePanel({ data, showOutcome }) {
  const { t } = useTranslation()
  const key = suffix => `natura_operations.confirmation_resend.${suffix}`
  const confirmedAt = formatTimestamp(data.externally_confirmed_at)

  return (
    <section className="mt-6 border border-gray-200 rounded p-4">
      {showOutcome && <OutcomeHeading error={data.error} />}

      {showOutcome && data.delivery && (
        <div role="status" className={`mb-4 p-3 border rounded text-sm ${DELIVERY_STYLE[data.delivery] || DELIVERY_STYLE[DELIVERY_UNKNOWN]}`}>
          <span className="font-medium">{t(key(`delivery_${data.delivery}`))}</span> {t(key(`delivery_${data.delivery}_explanation`))}
        </div>
      )}

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-4">
        <dt className="text-gray-500">{t(key('field_replenishment_id'))}</dt>
        <dd className="font-mono">{data.replenishment_id ?? '—'}</dd>

        <dt className="text-gray-500">{t(key('field_external_reference'))}</dt>
        <dd className="font-mono break-all">{data.external_reference || '—'}</dd>

        <dt className="text-gray-500">{t(key('field_store'))}</dt>
        <dd>
          {data.store_name || '—'}
          {data.store_id ? <span className="text-gray-500"> (id {data.store_id})</span> : null}
        </dd>

        <dt className="text-gray-500">{t(key('field_confirmed_at'))}</dt>
        <dd>{confirmedAt || t(key('never_confirmed'))}</dd>

        <dt className="text-gray-500">{t(key('field_path'))}</dt>
        <dd className="font-mono break-all">{data.request?.path || '—'}</dd>
      </dl>

      <div className="mb-4">
        <CopyButton text={buildTicketText(data, t)} label={t(key('copy_for_ticket'))} />
      </div>

      <JsonBlock heading={t(key('request_heading'))} body={data.request?.body} />

      {data.response && (
        <JsonBlock
          heading={data.response.code ? `${t(key('response_heading'))} (HTTP ${data.response.code})` : t(key('response_heading'))}
          body={data.response.body}
          emptyLabel={t(key('response_empty'))}
        />
      )}
    </section>
  )
}

EvidencePanel.propTypes = {
  data: PropTypes.object.isRequired,
  showOutcome: PropTypes.bool,
}

EvidencePanel.defaultProps = {
  showOutcome: false,
}
